import React from 'react';
import { Card, Divider } from '@material-ui/core';
import toast from 'react-hot-toast';

import { useResponsive } from '@project/hooks';
import { Button, KeyValueRow } from '@project/components';
import { useDownloadFile } from '@project/hooks/global';
import { invoiceService } from '@project/services';

type Props = {
  className?: string;
  columns?: React.ReactNode[];
  invoiceNumber?: string;
  invoiceId?: string;
  vehicleRegistration?: string;
  date?: string;
  invoiceAmount?: number;
  amountPaid?: number;
  paymentType?: string;
};

export const PaidInvoiceRow: React.FC<Props> = ({
  className,
  columns = [],
  invoiceNumber = '',
  invoiceId = '',
  invoiceAmount = 0,
  amountPaid = 0,
  paymentType = '',
}) => {
  const responsive = useResponsive();
  const { download: _downloadInvoiceContentPdf, loading: isLoadingDownloadPdf } = useDownloadFile({
    apiDefinition: () => invoiceService.getInvoicePdf(invoiceId),
    onError: () => toast.error('Could not download invoice'),
  });

  const downloadButton = (
    <Button
      className="bg-transparent shadow-none p-0"
      onClick={_downloadInvoiceContentPdf}
      disabled={isLoadingDownloadPdf}
    >
      <img
        src="assets/icons/download-blue-icon.png"
        alt="download"
        width={24}
        height={24}
        style={{ minWidth: 24 }}
      />
    </Button>
  );

  return responsive.isMobile ? (
    <Card className={`flex md:flex-row flex-col px-2 md:place-items-center ${className}`}>
      <div className="flex flex-row">
        <div className="flex flex-col w-full">
          <KeyValueRow keyString="Invoice Number:"> {invoiceNumber} </KeyValueRow>
          <KeyValueRow keyString="Invoice Amount:">
            <p className="text-sm">{`R ${invoiceAmount}`}</p>
          </KeyValueRow>
          <KeyValueRow keyString="Amount Paid:">
            <p className="text-sm">{`R ${amountPaid}`}</p>
          </KeyValueRow>
          <KeyValueRow keyString="Payment Type:"> {paymentType} </KeyValueRow>
        </div>
        <div className="flex justify-end py-2">{downloadButton}</div>
      </div>
    </Card>
  ) : (
    <Card className={`flex flex-row px-2 h-11 place-items-center ${className}`}>
      <div className="flex flex-1 flex-grow-5">
        {columns.length ? (
          columns[0]
        ) : (
          <div className="flex flex-col w-full">
            <KeyValueRow keyString="Invoice Number:"> {invoiceNumber} </KeyValueRow>
          </div>
        )}
      </div>
      {columns.length ? null : <Divider orientation="vertical" flexItem className="ml-16 mr-3" />}
      <div className="flex flex-1 flex-grow-1">
        {columns.length ? columns[1] : <p className="text-sm">{`R ${invoiceAmount}`}</p>}
      </div>
      {columns.length ? null : <Divider orientation="vertical" flexItem className="mx-3" />}
      <div className="flex flex-1 flex-grow-1">
        {columns.length ? columns[2] : <p className="text-sm">{`R ${amountPaid}`}</p>}
      </div>
      {columns.length ? null : <Divider orientation="vertical" flexItem className="mx-3" />}
      <div className="flex flex-1 flex-grow-1">
        {columns.length ? columns[3] : <p className="text-sm">{`${paymentType}`}</p>}
      </div>
      {columns.length ? null : <Divider orientation="vertical" flexItem className="mx-3" />}
      <div className="flex items-center justify-center w-10">
        {columns.length ? null : downloadButton}
      </div>
    </Card>
  );
};
